// Reusable form field wrapper matching existing design system
import { useState } from "react";
import { cn } from "../lib/utils";
import {
  ChevronDown,
  ChevronRight,
  Upload,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

export function Field({ label, required, hint, error, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </label>
      {children}
      {hint && !error && (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export function TextInput({ error, className, ...props }) {
  return (
    <input
      className={cn(
        "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring transition-colors placeholder:text-muted-foreground",
        error && "border-destructive focus:ring-destructive",
        className,
      )}
      {...props}
    />
  );
}

export function TextArea({ error, className, ...props }) {
  return (
    <textarea
      className={cn(
        "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring transition-colors placeholder:text-muted-foreground resize-none",
        error && "border-destructive focus:ring-destructive",
        className,
      )}
      rows={3}
      {...props}
    />
  );
}

export function SelectInput({ error, options, className, ...props }) {
  return (
    <select
      className={cn(
        "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring cursor-pointer transition-colors",
        error && "border-destructive",
        className,
      )}
      {...props}
    >
      {options.map((opt) => {
        if (typeof opt === "string")
          return (
            <option key={opt} value={opt}>
              {opt}
            </option>
          );
        return (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        );
      })}
    </select>
  );
}

export function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between">
      {(label || description) && (
        <div>
          {label && (
            <p className="text-sm font-medium text-foreground">{label}</p>
          )}
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 flex-shrink-0",
          checked ? "bg-accent" : "bg-muted",
        )}
        style={checked ? { background: "hsl(var(--accent))" } : {}}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
    </div>
  );
}

export function SectionDivider({ title, description }) {
  return (
    <div className="pt-4 pb-2 border-t border-border">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description && (
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      )}
    </div>
  );
}

export function FormActions({
  onSaveDraft,
  onPublish,
  onCancel,
  loading,
  publishLabel = "Publish",
}) {
  return (
    <div className="flex items-center gap-3 pt-4 border-t border-border">
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      {/* <button
        type="button"
        onClick={onSaveDraft}
        disabled={loading}
        className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
      >
        {loading ? "Saving..." : "Save Draft"}
      </button> */}
      <button
        type="button"
        onClick={onPublish}
        disabled={loading}
        className="btn-accent disabled:opacity-50"
      >
        {loading ? "Publishing..." : publishLabel}
      </button>
    </div>
  );
}

export function CollapsibleSection({
  title,
  active,
  onToggleActive,
  defaultOpen = true,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="section-panel">
      <div className="section-panel-header">
        <div className="flex items-center gap-3 flex-1">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {open ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <h2 className="section-panel-title">{title}</h2>
          {!active && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded">
              Inactive
            </span>
          )}
        </div>
        <Toggle checked={active} onChange={onToggleActive} />
      </div>
      {open && (
        <div
          className={cn(
            "section-panel-body space-y-4",
            !active && "opacity-50 pointer-events-none",
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function ImageUpload({
  label = "Click to upload image",
  hint = "JPG, PNG, WebP · Max 5MB",
}) {
  return (
    <div
      className="border-2 border-dashed border-border rounded-lg p-5 text-center hover:border-accent/40 transition-colors cursor-pointer"
      style={{ background: "hsl(var(--muted)/0.3)" }}
    >
      <Upload className="w-5 h-5 mx-auto text-muted-foreground mb-1.5" />
      <p className="text-sm font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
    </div>
  );
}

export function ReorderButtons({ index, total, onMove }) {
  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
        className="text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
      >
        <ArrowUp className="w-3 h-3" />
      </button>
      <button
        type="button"
        disabled={index === total - 1}
        onClick={() => onMove(index, index + 1)}
        className="text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
      >
        <ArrowDown className="w-3 h-3" />
      </button>
    </div>
  );
}

export function ImageUploadWithAlt({
  label = "Click to upload image",
  hint = "JPG, PNG, WebP · Max 5MB",
  alt,
  onAltChange,
}) {
  return (
    <div className="space-y-2">
      <ImageUpload label={label} hint={hint} />
      <TextInput
        value={alt}
        onChange={(e) => onAltChange(e.target.value)}
        placeholder="Alt text for accessibility"
      />
    </div>
  );
}

export function CTAGroup({
  label,
  text,
  url,
  newTab,
  onTextChange,
  onUrlChange,
  onNewTabChange,
}) {
  return (
    <div className="space-y-2 mt-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <TextInput
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder="Button text"
        />
        <TextInput
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="https://... or /path"
        />
      </div>
    </div>
  );
}

export function BackgroundControls({
  bgImageAlt,
  onBgImageAltChange,
  bgVideoUrl,
  onBgVideoUrlChange,
  overlayOpacity,
  onOverlayOpacityChange,
}) {
  return (
    <div className="pt-3 border-t border-border space-y-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Background Settings
      </p>
      <ImageUploadWithAlt
        label="Upload background image"
        hint="1920×1080px recommended"
        alt={bgImageAlt}
        onAltChange={onBgImageAltChange}
      />
      {/* <Field label="Background Video URL" hint="Optional MP4 or embed URL">
        <TextInput
          value={bgVideoUrl}
          onChange={(e) => onBgVideoUrlChange(e.target.value)}
          placeholder="https://..."
        />
      </Field> */}
    </div>
  );
}

export function autoSlug(title) {
  return (
    "/" +
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
  );
}
